import { FaCalculator, FaDownload, FaPlus, FaSpinner, FaTrash } from 'react-icons/fa';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';


const Api12Form = ({ state, updateState }) => {
    const CLIMATIQ_API_KEY = process.env.REACT_APP_CLIMATIQ_API_KEY || 'Y2ZBNWZKRzRGNzJYNjRQMzpa';

    const formData = state.formData || { year: new Date().getFullYear().toString() };
    const entries = state.entries || [];
    const inputMethod = state.inputMethod || 'throughput';
    const isCalculating = state.isCalculating || false;
    const isSearching = state.isSearching || false;
    const calculationResults = state.calculationResults || {};
    const availableFactors = state.availableFactors || {};

    // Process categories with EXACT payload formats from docs
    const processCategories = {
        'Calcination (lime)': {
            payload: {
                emission_factor: {
                    activity_id: "mined_materials-type_lime_market_for_lime",
                    source: "ecoinvent",
                    region: "RoW",
                    year: 2011,
                    source_lca_activity: "unknown",
                    data_version: "^0"
                },
                parameters: {
                    weight: 80,
                    weight_unit: "t"
                }
            },
            examples: ['Limestone processed — 120,000 t (95% CaCO₃)'],
            defaultUnit: 't'
        },
        'Roasting': {
            payload: {
                emission_factor: {
                    activity_id: "food-type_salt_curing_roast_poultry_cooked",
                    data_version: "^0"
                },
                parameters: {
                    weight: 80,
                    weight_unit: "t"
                }
            },
            examples: ['Copper concentrate roasting — 850,000 t'],
            defaultUnit: 't'
        },
        'Smelting': {
            payload: {
                emission_factor: {
                    activity_id: "construction-type_copper_smelting_facility_market_for_copper_smelting_facility",
                    data_version: "^0"
                },
                parameters: {
                    number: 1
                }
            },
            examples: ['Copper concentrate smelting — 850,000 t'],
            defaultUnit: 'unit'
        },
        'Converting': {
            payload: {
                emission_factor: {
                    activity_id: "construction-type_copper_smelting_facility_market_for_copper_smelting_facility",
                    data_version: "^0"
                },
                parameters: {
                    number: 1
                }
            },
            examples: ['Copper matte converting — 450,000 t'],
            defaultUnit: 'unit'
        },
        'Anode furnace': {
            payload: {
                emission_factor: {
                    activity_id: "construction-type_anode_factory_market_for_anode_factory",
                    source: "ecoinvent",
                    region: "GLOBAL",
                    year: 2011,
                    source_lca_activity: "unknown",
                    data_version: "^0"
                },
                parameters: {
                    number: 1
                }
            },
            examples: ['Anode furnace processing — 400,000 t'],
            defaultUnit: 'unit'
        },
        'Electro-refining': {
            payload: {
                emission_factor: {
                    activity_id: "metals-type_copper_cathode_electrorefining_of_copper_anode",
                    data_version: "^0"
                },
                parameters: {
                    weight: 80,
                    weight_unit: "t"
                }
            },
            examples: ['Copper electro-refining — 240,000 t'],
            defaultUnit: 't'
        },
        'Acid plant': {
            payload: {
                emission_factor: {
                    activity_id: "chemicals-type_sulfuric_acid_sulfuric_acid_production",
                    data_version: "^0"
                },
                parameters: {
                    weight: 80,
                    weight_unit: "t"
                }
            },
            examples: ['Sulfuric acid production — 950,000 t'],
            defaultUnit: 't'
        },
        'Other': {
            examples: ['Custom process — specify details'],
            defaultUnit: 't'
        }
    };

    // Common materials
    const processMaterials = {
        'Limestone': { typicalComposition: '95% CaCO₃' },
        'Dolomite': { typicalComposition: 'CaMg(CO₃)₂' },
        'Copper concentrate': { typicalComposition: '25-30% Cu' },
        'Copper matte': { typicalComposition: '60-70% Cu' },
        'Copper anode': { typicalComposition: '99% Cu' },
        'Copper cathode': { typicalComposition: '99.99% Cu' },
        'Sulfur': { typicalComposition: '99% S' },
        'Sulfuric acid': { typicalComposition: '98% H₂SO₄' },
        'Petroleum coke': { typicalComposition: '90-95% C' },
        'Carbon anodes': { typicalComposition: '85-95% C' },
        'Other': { typicalComposition: 'Specify composition' }
    };

    const carbonReagents = [
        'Petroleum coke anodes', 'Carbon reductants', 'Electrode paste',
        'Coal-based reductants', 'Coke', 'Graphite electrodes', 'Other carbon-bearing reagents'
    ];

    const handleYearChange = (year) => {
        updateState({
            formData: { ...formData, year },
            entries: entries.map(entry => ({
                ...entry,
                period: `${year} (Jan–Dec)`
            }))
        });
    };

    // Use EXACT payload formats from docs
    const getEmissionFactor = (processType) => {
        const config = processCategories[processType];
        if (!config || !config.payload) {
            return null;
        }

        const factor = {
            ...config.payload.emission_factor,
            name: `${processType} Process`,
            unit: config.payload.parameters.weight_unit || 'unit',
            unit_type: config.payload.parameters.weight_unit ? 'Mass' : 'Unit'
        };

        // Update available factors
        updateState({
            availableFactors: {
                ...availableFactors,
                [processType]: factor
            }
        });

        return factor;
    };

    const handleLoadFactors = () => {
        if (entries.length === 0) {
            toastr.error('Please add at least one process entry first');
            return;
        }

        updateState({ isSearching: true, availableFactors: {} });
        toastr.info('Loading predefined emission factors...');

        try {
            const processesToLoad = [...new Set(entries.map(entry => entry.processStep).filter(Boolean))];
            let foundCount = 0;

            for (const process of processesToLoad) {
                const factor = getEmissionFactor(process);
                if (factor) foundCount++;
            }

            if (foundCount > 0) {
                toastr.success(`Loaded emission factors for ${foundCount} process(es)!`);
            } else {
                toastr.warning('No predefined factors found for selected processes.');
            }
        } catch (error) {
            toastr.error('Failed to load factors.');
        } finally {
            updateState({ isSearching: false });
        }
    };

    // API call function with EXACT payload format from docs
    const callClimateIQAPI = async (entry, emissionFactor) => {
        try {
            const processConfig = processCategories[entry.processStep];
            if (!processConfig || !processConfig.payload) {
                throw new Error(`No payload configuration found for ${entry.processStep}`);
            }

            // Create EXACT payload structure from docs
            let requestBody = JSON.parse(JSON.stringify(processConfig.payload));

            // Update parameters with actual values
            if (inputMethod === 'throughput') {
                const quantity = parseFloat(entry.tonnesProcessed);
                if (!quantity || quantity <= 0) {
                    throw new Error('Invalid quantity for throughput calculation');
                }

                if (requestBody.parameters.weight !== undefined) {
                    requestBody.parameters.weight = quantity;
                } else if (requestBody.parameters.number !== undefined) {
                    requestBody.parameters.number = quantity;
                }
            } else if (inputMethod === 'measured') {
                const co2Mass = parseFloat(entry.annualCO2);
                if (!co2Mass || co2Mass <= 0) {
                    throw new Error('Invalid CO2 mass for direct measurement');
                }

                // For direct measurements, use the payload structure but with CO2 weight
                if (requestBody.parameters.weight !== undefined) {
                    requestBody.parameters.weight = co2Mass;
                    requestBody.parameters.weight_unit = "t";
                }
            } else if (inputMethod === 'carbon') {
                const reagentMass = parseFloat(entry.tonnesConsumed);
                if (!reagentMass || reagentMass <= 0) {
                    throw new Error('Invalid reagent mass for carbon calculation');
                }

                const carbonPercent = parseFloat(entry.carbonPercent) || 90;
                const carbonMass = reagentMass * (carbonPercent / 100);
                const co2Mass = carbonMass * (44 / 12);

                if (requestBody.parameters.weight !== undefined) {
                    requestBody.parameters.weight = co2Mass;
                    requestBody.parameters.weight_unit = "t";
                }
            } else if (inputMethod === 'acid') {
                const quantity = parseFloat(entry.acidOutput || entry.sulfurFeed);
                if (!quantity || quantity <= 0) {
                    throw new Error('Invalid quantity for acid calculation');
                }

                if (requestBody.parameters.weight !== undefined) {
                    requestBody.parameters.weight = quantity;
                    requestBody.parameters.weight_unit = "t";
                }
            }

            console.log(`API Request for ${entry.processStep}:`, JSON.stringify(requestBody, null, 2));

            const response = await fetch('https://api.climatiq.io/data/v1/estimate', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`API request failed with status ${response.status}: ${errorData}`);
            }

            return await response.json();

        } catch (error) {
            console.error(`Error calling ClimateIQ API for ${entry.processStep}:`, error);
            throw error;
        }
    };

    const handleCalculate = async () => {
        if (entries.length === 0) {
            toastr.error('Please add at least one process entry');
            return;
        }

        const validationErrors = validateEntries();
        if (validationErrors.length > 0) {
            validationErrors.forEach(error => toastr.error(error));
            return;
        }

        updateState({ isCalculating: true, calculationResults: {} });
        toastr.info('Calculating process emissions via API...');

        try {
            const results = {};
            let hasSuccessfulCalculation = false;

            for (let i = 0; i < entries.length; i++) {
                const entry = entries[i];

                try {
                    let result = null;
                    const emissionFactor = availableFactors[entry.processStep];
                    const processConfig = processCategories[entry.processStep];

                    // Method 1: Direct measurement (bypass API calculation, use direct value)
                    if (inputMethod === 'measured') {
                        const co2Mass = parseFloat(entry.annualCO2);

                        result = {
                            processStep: entry.processStep,
                            description: entry.stackId,
                            quantity: co2Mass,
                            quantity_unit: 't CO₂',
                            co2e: co2Mass,
                            co2e_unit: 't',
                            calculation_method: 'direct_measurement',
                            activity_data: { weight: co2Mass, weight_unit: 't' },
                            emission_factor: null,
                            period: entry.period,
                            calculation_type: 'direct_measurement'
                        };
                    }
                    // Method 2: API calculation with predefined factor
                    else if (emissionFactor && emissionFactor.activity_id && processConfig && processConfig.payload) {
                        try {
                            const apiResult = await callClimateIQAPI(entry, emissionFactor);
                            result = {
                                processStep: entry.processStep,
                                description: getEntryDescription(entry),
                                quantity: getQuantity(entry),
                                quantity_unit: getQuantityUnit(entry),
                                co2e: apiResult.co2e,
                                co2e_unit: apiResult.co2e_unit,
                                calculation_method: apiResult.co2e_calculation_method,
                                activity_data: apiResult.activity_data,
                                emission_factor: emissionFactor,
                                period: entry.period,
                                calculation_type: 'api_factor',
                                api_request: processConfig.payload
                            };
                        } catch (apiError) {
                            console.error(`API calculation failed for ${entry.processStep}:`, apiError);
                            toastr.error(`API calculation failed for ${entry.processStep}: ${apiError.message}`);
                            continue;
                        }
                    }
                    else {
                        toastr.warning(`No emission factor available for ${entry.processStep}. Please load factors first.`);
                        continue;
                    }

                    if (result) {
                        const resultKey = `process_${i}`;
                        results[resultKey] = result;

                        const methodType = result.calculation_type === 'direct_measurement' ? 'measured' : 'API factor';

                        toastr.success(`${entry.processStep}: ${result.co2e.toFixed(2)} t CO₂e (${methodType})`);
                        hasSuccessfulCalculation = true;
                    }

                } catch (error) {
                    console.error(`Calculation error for process entry ${i + 1}:`, error);
                    toastr.error(`Failed to calculate ${entry.processStep}: ${error.message}`);
                }
            }

            updateState({ calculationResults: results });

            if (hasSuccessfulCalculation) {
                const totalEmissions = Object.values(results).reduce((sum, result) => sum + (result.co2e || 0), 0);
                toastr.success(`Calculation complete! Total: ${totalEmissions.toFixed(2)} t CO₂e`);
            } else {
                toastr.warning('No emissions could be calculated. Please ensure you have emission factors for all processes.');
            }

        } catch (error) {
            toastr.error('Calculation failed. Please check your inputs and try again.');
            console.error('Calculation error:', error);
        } finally {
            updateState({ isCalculating: false });
        }
    };

    // Helper functions
    const getQuantity = (entry) => {
        switch (inputMethod) {
            case 'throughput': return parseFloat(entry.tonnesProcessed);
            case 'measured': return parseFloat(entry.annualCO2);
            case 'carbon': return parseFloat(entry.tonnesConsumed);
            case 'acid': return parseFloat(entry.acidOutput || entry.sulfurFeed);
            default: return 0;
        }
    };

    const getQuantityUnit = (entry) => {
        switch (inputMethod) {
            case 'throughput': return 't';
            case 'measured': return 't CO₂';
            case 'carbon': return 't reagent';
            case 'acid': return entry.acidOutput ? 't acid' : 't sulfur';
            default: return 't';
        }
    };

    const getEntryDescription = (entry) => {
        switch (inputMethod) {
            case 'throughput': return entry.materialProcessed;
            case 'measured': return entry.stackId;
            case 'carbon': return entry.reagentName;
            case 'acid': return entry.acidOutput ? 'Acid Output' : 'Sulfur Feed';
            default: return entry.processStep;
        }
    };

    const validateEntries = () => {
        const errors = [];

        entries.forEach((entry, index) => {
            if (!entry.processStep) {
                errors.push(`Entry ${index + 1}: Please select a process step`);
            }
            if (!entry.period) {
                errors.push(`Entry ${index + 1}: Please enter a reporting period`);
            }

            if (inputMethod === 'throughput') {
                if (!entry.tonnesProcessed || parseFloat(entry.tonnesProcessed) <= 0) {
                    errors.push(`Entry ${index + 1}: Please enter valid tonnes processed`);
                }
                if (!entry.materialProcessed) {
                    errors.push(`Entry ${index + 1}: Please specify material processed`);
                }
            } else if (inputMethod === 'measured') {
                if (!entry.annualCO2 || parseFloat(entry.annualCO2) <= 0) {
                    errors.push(`Entry ${index + 1}: Please enter valid annual CO₂ measurement`);
                }
                if (!entry.stackId) {
                    errors.push(`Entry ${index + 1}: Please specify stack ID/process area`);
                }
            } else if (inputMethod === 'carbon') {
                if (!entry.tonnesConsumed || parseFloat(entry.tonnesConsumed) <= 0) {
                    errors.push(`Entry ${index + 1}: Please enter valid tonnes consumed`);
                }
                if (!entry.reagentName) {
                    errors.push(`Entry ${index + 1}: Please specify reagent name`);
                }
            } else if (inputMethod === 'acid') {
                if (!entry.acidOutput && !entry.sulfurFeed) {
                    errors.push(`Entry ${index + 1}: Please provide either acid output or sulfur feed`);
                }
            }
        });

        return errors;
    };

    const handleAddEntry = () => {
        updateState({
            entries: [...entries, {
                processStep: '',
                materialProcessed: '',
                tonnesProcessed: '',
                composition: '',
                stackId: '',
                annualCO2: '',
                measurementMethod: 'CEMS',
                reagentName: '',
                tonnesConsumed: '',
                carbonPercent: '',
                sulfurFeed: '',
                acidOutput: '',
                processDescription: '',
                period: `${formData.year} (Jan–Dec)`,
                evidence: null,
            }]
        });
    };

    const handleEntryChange = (index, field, value) => {
        const updated = [...entries];
        updated[index][field] = value;

        // Auto-set typical composition when materials are selected
        if (field === 'materialProcessed' && value && processMaterials[value]) {
            updated[index].composition = processMaterials[value].typicalComposition;
        }

        // Auto-set carbon percentage for common reagents
        if (field === 'reagentName' && value) {
            if (value.includes('petroleum')) updated[index].carbonPercent = '92';
            else if (value.includes('coke')) updated[index].carbonPercent = '90';
            else if (value.includes('electrode')) updated[index].carbonPercent = '85';
        }

        updateState({ entries: updated });
    };

    const handleRemoveEntry = (index) => {
        updateState({ entries: entries.filter((_, i) => i !== index) });
    };

    const loadSampleData = () => {
        updateState({
            formData: { ...formData, year: '2025' },
            entries: [{
                processStep: 'Calcination (lime)',
                materialProcessed: 'Limestone',
                tonnesProcessed: '120000',
                composition: '95% CaCO₃',
                stackId: '',
                annualCO2: '',
                measurementMethod: 'CEMS',
                reagentName: '',
                tonnesConsumed: '',
                carbonPercent: '',
                sulfurFeed: '',
                acidOutput: '',
                processDescription: 'Lime production kiln for flux preparation',
                period: '2025 (Jan–Dec)',
                evidence: null,
            }]
        });
        toastr.info('Sample calcination data loaded. Click "Load Factors" to get emission factors.');
    };

    // Calculate totals for display
    const totalEmissions = Object.values(calculationResults).reduce((sum, result) => sum + (result.co2e || 0), 0);
    const totalThroughput = entries.reduce((sum, entry) => sum + (parseFloat(entry.tonnesProcessed) || 0), 0);

    // Count calculation methods
    const calculationMethods = {
        direct_measurement: Object.values(calculationResults).filter(r => r.calculation_type === 'direct_measurement').length,
        api_factor: Object.values(calculationResults).filter(r => r.calculation_type === 'api_factor').length
    };

    return (
        <div className="max-w-6xl mx-auto px-4 py-8 pb-24">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-8">
                    {/* API Key Status */}
                    <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                            <strong>API Status:</strong> {CLIMATIQ_API_KEY && CLIMATIQ_API_KEY !== 'Y2ZBNWZKRzRGNzJYNjRQMzPa' ?
                                '✅ API Key Configured' : '❌ Please set REACT_APP_CLIMATIQ_API_KEY in .env file'}
                        </p>
                        <p className="text-xs text-yellow-700 mt-1">
                            <strong>Note:</strong> Using EXACT Climatiq payload formats as provided.
                        </p>
                    </div>

                    {/* KPI Summary */}
                    {(entries.length > 0 || Object.keys(calculationResults).length > 0) && (
                        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                <h3 className="text-sm font-semibold text-blue-800">Total Processes</h3>
                                <p className="text-2xl font-bold text-blue-600">{entries.length}</p>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                <h3 className="text-sm font-semibold text-green-800">Total Emissions</h3>
                                <p className="text-2xl font-bold text-green-600">{totalEmissions.toFixed(0)} t CO₂e</p>
                            </div>
                            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                                <h3 className="text-sm font-semibold text-purple-800">Total Throughput</h3>
                                <p className="text-2xl font-bold text-purple-600">{totalThroughput.toLocaleString()} t</p>
                            </div>
                            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                                <h3 className="text-sm font-semibold text-orange-800">Calculation Methods</h3>
                                <p className="text-lg font-bold text-orange-600">
                                    {calculationMethods.direct_measurement} Direct, {calculationMethods.api_factor} API
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Year Selection */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reporting Year *
                        </label>
                        <select
                            value={formData.year}
                            onChange={(e) => handleYearChange(e.target.value)}
                            className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            {Array.from({ length: new Date().getFullYear() - 2009 }, (_, i) =>
                                (new Date().getFullYear() - i).toString()
                            ).map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>

                    {/* Input Method Selection */}
                    <div className="mb-8">
                        <h3 className="text-lg font-semibold text-gray-700 mb-4">12.1 Process Emissions Calculation Method</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                            {[
                                { key: 'throughput', label: 'Throughput', desc: 'Material processed with API factors' },
                                { key: 'measured', label: 'Direct Measurements', desc: 'CEMS/stack test data via API' },
                                { key: 'carbon', label: 'Carbon Reagents', desc: 'Carbon-bearing inputs via API' },
                                { key: 'acid', label: 'Acid/Sulfur Inputs', desc: 'Acid plant operations via API' }
                            ].map(method => (
                                <button
                                    key={method.key}
                                    onClick={() => updateState({ inputMethod: method.key })}
                                    className={`p-4 rounded-lg border-2 transition-all text-left ${inputMethod === method.key
                                            ? 'border-green-500 bg-green-50 text-green-700'
                                            : 'border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100'
                                        }`}
                                >
                                    <div className="font-semibold text-sm">{method.label}</div>
                                    <div className="text-xs mt-1">{method.desc}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Load Factors Button */}
                    <div className="mb-6">
                        <button
                            onClick={handleLoadFactors}
                            disabled={isSearching}
                            className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSearching ? (
                                <FaSpinner className="w-4 h-4 animate-spin" />
                            ) : (
                                <FaDownload className="w-4 h-4" />
                            )}
                            <span>{isSearching ? 'Loading...' : 'Load Emission Factors'}</span>
                        </button>
                        <p className="text-xs text-gray-500 mt-2">
                            Load predefined Climatiq emission factors with exact payload formats.
                        </p>
                    </div>

                    {/* Available Factors Display */}
                    {Object.keys(availableFactors).length > 0 && (
                        <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                            <h3 className="font-semibold text-green-800 mb-2">Available Process Emission Factors</h3>
                            <div className="space-y-2">
                                {Object.entries(availableFactors).map(([process, factor]) => (
                                    <div key={process} className="text-sm text-green-700">
                                        <strong>{process}:</strong> {factor.activity_id}
                                        <br />
                                        <span className="text-xs">
                                            Source: {factor.source || 'N/A'} | Region: {factor.region || 'N/A'} | Data Version: {factor.data_version}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* No Factors Warning */}
                    {Object.keys(availableFactors).length === 0 && entries.length > 0 && (
                        <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
                            <h3 className="font-semibold text-red-800 mb-2">No Emission Factors Loaded</h3>
                            <div className="text-sm text-red-700 space-y-1">
                                <p>Please click "Load Emission Factors" to get predefined API factors for your processes.</p>
                                <p>Calculations cannot proceed without emission factors from the ClimateIQ API.</p>
                            </div>
                        </div>
                    )}

                    <div className="mb-8">
                        <h3 className="text-lg font-semibold text-gray-700 mb-4">
                            12.2 Process Emissions Data
                        </h3>

                        <button onClick={handleAddEntry} className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 mb-6">
                            <FaPlus className="w-4 h-4" />
                            <span>Add Process Entry</span>
                        </button>

                        {entries.map((entry, index) => {
                            const resultKey = `process_${index}`;
                            const result = calculationResults[resultKey];
                            const processConfig = processCategories[entry.processStep];
                            const materialConfig = entry.materialProcessed ? processMaterials[entry.materialProcessed] : null;
                            const factor = availableFactors[entry.processStep];

                            return (
                                <div key={index} className="p-6 border-2 border-gray-200 rounded-lg space-y-4 mb-4">
                                    <div className="flex justify-between items-center">
                                        <h4 className="font-semibold">Process Entry {index + 1}</h4>
                                        <button onClick={() => handleRemoveEntry(index)} className="text-red-500 hover:text-red-700">
                                            <FaTrash className="w-4 h-4" />
                                        </button>
                                    </div>

                                    {/* Calculation Result */}
                                    {result && (
                                        <div className={`p-3 rounded-lg border ${result.calculation_type === 'direct_measurement' ? 'bg-green-100 border-green-200' :
                                                'bg-blue-100 border-blue-200'
                                            }`}>
                                            <p className="text-sm font-medium">
                                                <span className={
                                                    result.calculation_type === 'direct_measurement' ? 'text-green-800' : 'text-blue-800'
                                                }>
                                                    Emissions: {result.co2e.toFixed(2)} {result.co2e_unit}
                                                </span>
                                            </p>
                                            <p className={`text-xs ${result.calculation_type === 'direct_measurement' ? 'text-green-600' : 'text-blue-600'
                                                }`}>
                                                {result.quantity} {result.quantity_unit} • {result.calculation_method}
                                            </p>
                                        </div>
                                    )}

                                    {/* Emission Factor Info */}
                                    {factor && processConfig && (
                                        <div className="p-3 bg-blue-100 rounded-lg border border-blue-200">
                                            <p className="text-xs text-blue-800">
                                                <strong>Emission Factor:</strong> {factor.activity_id}
                                                <br />
                                                <strong>Payload Type:</strong> {processConfig.payload.parameters.weight ? 'Weight-based' : 'Unit-based'}
                                                <br />
                                                <strong>Data Version:</strong> {factor.data_version}
                                            </p>
                                            <details className="mt-2">
                                                <summary className="text-xs cursor-pointer text-blue-600">View Exact Payload</summary>
                                                <pre className="text-xs mt-1 p-2 bg-blue-50 rounded overflow-auto">
                                                    {JSON.stringify(processConfig.payload, null, 2)}
                                                </pre>
                                            </details>
                                        </div>
                                    )}

                                    {/* No Factor Warning */}
                                    {!factor && entry.processStep && (
                                        <div className="p-3 bg-red-100 rounded-lg border border-red-200">
                                            <p className="text-xs text-red-800">
                                                <strong>No emission factor loaded.</strong> Click "Load Emission Factors" to get API factor for {entry.processStep}.
                                            </p>
                                        </div>
                                    )}

                                    {/* Process Information Helper */}
                                    {processConfig && (
                                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                            <div className="text-sm text-blue-700">
                                                <strong>Example:</strong> {processConfig.examples[0]}
                                                {processConfig.payload && (
                                                    <><br /><strong>Payload Type:</strong> {processConfig.payload.parameters.weight ? 'Weight-based' : 'Unit-based'}</>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Material Information Helper */}
                                    {materialConfig && (
                                        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                                            <p className="text-xs text-green-700">
                                                <strong>Typical Composition:</strong> {materialConfig.typicalComposition}
                                            </p>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Common Fields */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Process Step *</label>
                                            <select
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                value={entry.processStep}
                                                onChange={(e) => handleEntryChange(index, 'processStep', e.target.value)}
                                            >
                                                <option value="">Select process step</option>
                                                {Object.keys(processCategories).map(step => (
                                                    <option key={step} value={step}>{step}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Reporting Period *</label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                placeholder="e.g., 2024 (Jan–Dec)"
                                                value={entry.period}
                                                onChange={(e) => handleEntryChange(index, 'period', e.target.value)}
                                            />
                                        </div>

                                        {/* Throughput Method Fields */}
                                        {inputMethod === 'throughput' && (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Material Processed *</label>
                                                    <select
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        value={entry.materialProcessed}
                                                        onChange={(e) => handleEntryChange(index, 'materialProcessed', e.target.value)}
                                                    >
                                                        <option value="">Select material</option>
                                                        {Object.keys(processMaterials).map(material => (
                                                            <option key={material} value={material}>{material}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        {processConfig && processConfig.payload.parameters.number ? 'Number of Units' : 'Tonnes Processed'} *
                                                    </label>
                                                    <input
                                                        type="number"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        placeholder={processConfig && processConfig.payload.parameters.number ? 'Enter number of units' : 'Enter tonnes'}
                                                        value={entry.tonnesProcessed}
                                                        onChange={(e) => handleEntryChange(index, 'tonnesProcessed', e.target.value)}
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Composition/Specification</label>
                                                    <input
                                                        type="text"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        placeholder="e.g., CaCO₃ 95%, LOI 42%"
                                                        value={entry.composition}
                                                        onChange={(e) => handleEntryChange(index, 'composition', e.target.value)}
                                                    />
                                                </div>
                                            </>
                                        )}

                                        {/* Measured Method Fields */}
                                        {inputMethod === 'measured' && (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Stack ID / Process Area *</label>
                                                    <input
                                                        type="text"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        placeholder="e.g., S-04, Kiln Stack"
                                                        value={entry.stackId}
                                                        onChange={(e) => handleEntryChange(index, 'stackId', e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Annual CO₂ (t) *</label>
                                                    <input
                                                        type="number"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        placeholder="Enter tonnes CO₂"
                                                        value={entry.annualCO2}
                                                        onChange={(e) => handleEntryChange(index, 'annualCO2', e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Measurement Method</label>
                                                    <select
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        value={entry.measurementMethod}
                                                        onChange={(e) => handleEntryChange(index, 'measurementMethod', e.target.value)}
                                                    >
                                                        <option value="CEMS">CEMS (Continuous)</option>
                                                        <option value="Stack Test">Stack Test</option>
                                                        <option value="Calculation">Calculation</option>
                                                        <option value="Other">Other</option>
                                                    </select>
                                                </div>
                                            </>
                                        )}

                                        {/* Carbon Method Fields */}
                                        {inputMethod === 'carbon' && (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Reagent Name *</label>
                                                    <select
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        value={entry.reagentName}
                                                        onChange={(e) => handleEntryChange(index, 'reagentName', e.target.value)}
                                                    >
                                                        <option value="">Select reagent</option>
                                                        {carbonReagents.map(reagent => (
                                                            <option key={reagent} value={reagent}>{reagent}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Tonnes Consumed *</label>
                                                    <input
                                                        type="number"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        placeholder="Enter tonnes"
                                                        value={entry.tonnesConsumed}
                                                        onChange={(e) => handleEntryChange(index, 'tonnesConsumed', e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Carbon Content (%) *</label>
                                                    <input
                                                        type="number"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        placeholder="e.g., 97"
                                                        value={entry.carbonPercent}
                                                        onChange={(e) => handleEntryChange(index, 'carbonPercent', e.target.value)}
                                                    />
                                                </div>
                                            </>
                                        )}

                                        {/* Acid Method Fields */}
                                        {inputMethod === 'acid' && (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Sulfur Feed (t)</label>
                                                    <input
                                                        type="number"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        placeholder="Enter tonnes sulfur"
                                                        value={entry.sulfurFeed}
                                                        onChange={(e) => handleEntryChange(index, 'sulfurFeed', e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Acid Output (t)</label>
                                                    <input
                                                        type="number"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        placeholder="Enter tonnes acid"
                                                        value={entry.acidOutput}
                                                        onChange={(e) => handleEntryChange(index, 'acidOutput', e.target.value)}
                                                    />
                                                </div>
                                            </>
                                        )}

                                        {/* Process Description */}
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Process Description</label>
                                            <textarea
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                rows="2"
                                                placeholder="Describe the process, equipment, and any relevant details..."
                                                value={entry.processDescription}
                                                onChange={(e) => handleEntryChange(index, 'processDescription', e.target.value)}
                                            />
                                        </div>

                                        {/* Evidence Upload */}
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Evidence Upload</label>
                                            <input
                                                type="file"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                onChange={(e) => handleEntryChange(index, 'evidence', e.target.files[0])}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Total Results */}
                    {Object.keys(calculationResults).length > 0 && (
                        <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Process Emissions Summary</h3>
                            <div className="space-y-3">
                                {Object.entries(calculationResults).map(([key, result]) => (
                                    <div key={key} className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200">
                                        <div className="flex-1">
                                            <div className="font-medium text-gray-700">
                                                {result.processStep}
                                                <span className={`ml-2 text-xs px-2 py-1 rounded-full ${result.calculation_type === 'direct_measurement' ? 'bg-green-100 text-green-800' :
                                                        'bg-blue-100 text-blue-800'
                                                    }`}>
                                                    {result.calculation_type === 'direct_measurement' ? 'Measured' : 'API Factor'}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-500">
                                                {result.quantity} {result.quantity_unit} • {result.calculation_method}
                                            </p>
                                        </div>
                                        <span className="font-semibold text-green-700 text-lg">
                                            {result.co2e.toFixed(2)} {result.co2e_unit}
                                        </span>
                                    </div>
                                ))}
                                <div className="border-t border-gray-300 pt-3 mt-2">
                                    <div className="flex justify-between items-center font-bold text-lg">
                                        <span>Total Process Emissions (Scope 1):</span>
                                        <span className="text-blue-700">
                                            {totalEmissions.toFixed(2)} t CO₂e
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            onClick={loadSampleData}
                            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all duration-200 text-sm"
                        >
                            Load Sample Data
                        </button>
                        <button
                            onClick={handleCalculate}
                            disabled={isCalculating || Object.keys(availableFactors).length === 0}
                            className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isCalculating ? (
                                <FaSpinner className="w-4 h-4 animate-spin" />
                            ) : (
                                <FaCalculator className="w-4 h-4" />
                            )}
                            <span>{isCalculating ? 'Calculating...' : 'Calculate via API'}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Api12Form;